// src/component/NavBar.js
import { useState, useEffect } from "react";
import { Navbar, Nav, Container } from "react-bootstrap";
import photo from "../assets/img/icon.png";

export const NavBar = ({ isDarkMode }) => {
  const [activeLink, setActiveLink] = useState("home");
  const [scrolled,   setScrolled]   = useState(false);
  const [menuOpen,   setMenuOpen]   = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onUpdateActiveLink = (value) => {
    setActiveLink(value);
    setMenuOpen(false);
  };

  const NAV_LINKS = [
    { id: "home",         label: "Home"       },
    { id: "pricing",      label: "Pricing"    },
    { id: "services",     label: "Services"   },
    { id: "about-section",label: "About"      },
    { id: "apply",        label: "Apply"      },
    { id: "follow",       label: "Contact Us" },
  ];

  return (
    <>
      <Navbar
        expand="md"
        expanded={menuOpen}
        onToggle={setMenuOpen}
        style={{
          ...styles.navbar,
          background: scrolled
            ? "rgba(8,15,30,0.92)"
            : "rgba(8,15,30,0.6)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          boxShadow: scrolled
            ? "0 2px 32px rgba(0,0,0,0.5), 0 1px 0 rgba(49,179,186,0.15)"
            : "none",
        }}
      >
        <Container>
          {/* ── Logo & brand ───────────────────────────── */}
          <Navbar.Brand href="#home" style={styles.brand}
            onClick={() => onUpdateActiveLink("home")}>
            <div style={styles.logoRing}>
              <img src={photo} alt="Logo" style={styles.logoImg} />
            </div>
            <div style={styles.brandText}>
              <span style={styles.brandName}>Ethio Exam</span>
              <span style={styles.brandAccent}>Mastery</span>
            </div>
          </Navbar.Brand>

          {/* ── Mobile toggle ──────────────────────────── */}
          <Navbar.Toggle
            aria-controls="main-nav"
            style={styles.toggle}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  style={{
                    ...styles.toggleBar,
                    width: i === 1 ? 18 : 24,
                    background: "#31b3ba",
                  }}
                />
              ))}
            </div>
          </Navbar.Toggle>

          {/* ── Nav links ──────────────────────────────── */}
          <Navbar.Collapse id="main-nav">
            <Nav className="ms-auto" style={styles.navList}>
              {NAV_LINKS.map((link) => {
                const isActive = activeLink === link.id;
                return (
                  <Nav.Link
                    key={link.id}
                    href={`#${link.id}`}
                    onClick={() => onUpdateActiveLink(link.id)}
                    style={{
                      ...styles.navLink,
                      color: isActive ? "#31b3ba" : "rgba(255,255,255,0.75)",
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) e.currentTarget.style.color = "#fff";
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive)
                        e.currentTarget.style.color = "rgba(255,255,255,0.75)";
                    }}
                  >
                    {link.label}
                    {isActive && <span style={styles.activeDot} />}
                  </Nav.Link>
                );
              })}

              {/* Telegram CTA in navbar */}
              <a
                href="https://t.me/exammastery_support"
                target="_blank"
                rel="noreferrer"
                style={styles.telegramBtn}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background =
                    "linear-gradient(135deg, #31b3ba, #1a8f96)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 24px rgba(49,179,186,0.45)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                ✈️ Telegram
              </a>
            </Nav>
          </Navbar.Collapse>
        </Container>

        {/* Progress line at bottom of navbar */}
        <div style={styles.progressLine} />
      </Navbar>

      {/* Spacer so content doesn't hide under fixed navbar */}
      <div style={{ height: 68 }} />
    </>
  );
};

const styles = {
  navbar: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    padding: "10px 0",
    transition: "background 0.3s ease, box-shadow 0.3s ease",
    borderBottom: "1px solid rgba(49,179,186,0.08)",
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    textDecoration: "none",
  },
  logoRing: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    padding: 2,
    background: "linear-gradient(135deg, #31b3ba, #a855f7)",
    flexShrink: 0,
  },
  logoImg: {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    objectFit: "cover",
    display: "block",
    border: "2px solid #080f1e",
  },
  brandText: {
    display: "flex",
    flexDirection: "column",
    lineHeight: 1.1,
  },
  brandName: {
    color: "#fff",
    fontWeight: 900,
    fontSize: "0.95rem",
    letterSpacing: "0.5px",
  },
  brandAccent: {
    background: "linear-gradient(135deg, #31b3ba, #a855f7)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    fontWeight: 900,
    fontSize: "0.95rem",
    letterSpacing: "0.5px",
  },
  toggle: {
    border: "1px solid rgba(49,179,186,0.3)",
    borderRadius: 8,
    padding: "6px 10px",
    background: "rgba(49,179,186,0.08)",
  },
  toggleBar: {
    display: "block",
    height: 2,
    borderRadius: 2,
    transition: "all 0.3s ease",
  },
  navList: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    flexWrap: "wrap",
  },
  navLink: {
    position: "relative",
    fontWeight: 600,
    fontSize: "0.88rem",
    padding: "8px 12px",
    borderRadius: 8,
    transition: "color 0.2s ease",
    letterSpacing: "0.3px",
  },
  activeDot: {
    position: "absolute",
    bottom: 2,
    left: "50%",
    transform: "translateX(-50%)",
    width: 4,
    height: 4,
    borderRadius: "50%",
    background: "#31b3ba",
    display: "block",
  },
  telegramBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "transparent",
    border: "1.5px solid rgba(49,179,186,0.5)",
    color: "#31b3ba",
    borderRadius: 10,
    padding: "7px 16px",
    fontSize: "0.85rem",
    fontWeight: 700,
    textDecoration: "none",
    marginLeft: 8,
    transition: "all 0.25s ease",
    whiteSpace: "nowrap",
  },
  progressLine: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 1,
    background:
      "linear-gradient(90deg, transparent, rgba(49,179,186,0.4), rgba(168,85,247,0.3), transparent)",
  },
};